import { Component, OnInit, Input, ViewEncapsulation, Output, EventEmitter, OnDestroy } from '@angular/core';
import { FormGroup, FormBuilder, Validators, ReactiveFormsModule } from '@angular/forms';
import { ConfirmCodeOption } from './models';
import { take, Subscription } from 'rxjs'

import { RequestConfirmationService } from './services/request-confirmation.service';
import { LogHelper } from '@app/shared/helper';
import { UserInfoService } from '@app/core/services/state/user-info/userInfo.service';
import { UserInfo } from '@app/core/models';
import { ConfirmCodeType, ConfirmCodeSendType, ConfirmCodeSecurityType } from './types';
import { RequestType } from '@app/shared/types';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { MaterialModule } from '@app/shared/material.module';
import { DirectivesModule } from '@app/shared/directives/directives.module';
import { CountdownNewComponent } from '../../countdown/countdown-new.component';

@Component({
    selector: 'tc-request-confirmation',
    imports: [
        CommonModule,
        TranslateModule,
        ReactiveFormsModule,
        MaterialModule,
        DirectivesModule,
        CountdownNewComponent
    ],
    templateUrl: './request-confirmation.component.html',
    styleUrls: ['./request-confirmation.component.scss'],
    encapsulation: ViewEncapsulation.None
})
export class RequestConfirmationComponent implements OnInit {
  @Output() secureCodeEmit = new EventEmitter<string>();
  @Output() securityIsDisabledEmit = new EventEmitter<boolean>();
  @Input() option: ConfirmCodeOption;
  @Input() informationAccepted: boolean;
  @Input() requestType: RequestType;
  @Input() isNewCoreApi: boolean = false;

  // observable
  submitSubscription: Subscription;

  // form
  form: FormGroup;

  // data
  interval: number;
  userInfoData: UserInfo;
  confirmationHelpText: string;

  // boolean
  timeIsExpired = true;
  enableConfirmation: boolean;

  // types
  sendType: ConfirmCodeSendType;
  confirmCodeSendType = ConfirmCodeSendType;
  activeConfirmCodeType: ConfirmCodeType;

  constructor(
    private fb: FormBuilder,
    private userInfoService: UserInfoService,
    private requestConfirmationService: RequestConfirmationService
  ) { }

  ngOnInit() {
    this.initForm();
    this.initConfirmCode();
    this.onSecureCodeChange();
  }

  initForm(): void {
    this.form = this.fb.group({
      email: [''],
      secureCode: ['', [Validators.required, Validators.minLength(6), Validators.maxLength(6)]]
    });
    this.form.controls['email'].disable();
  }

  get f(): any {
    return this.form.controls;
  }

  sendCode(): void {
    this.submitSubscription = this.requestConfirmationService
      .sendCodeForRequestConfirm(this.requestType, this.isNewCoreApi)
      .pipe(take(1))
      .subscribe((response: any) => {
        this.handelResponseForExpireTime(response);
        document.getElementById('codeInput').click();
      });

  }

  private handelResponseForExpireTime(response: any): void {
    const expierTimeSec = response['expireTimeSec'];
    this.interval = expierTimeSec;
    this.timeIsExpired = false;
  }

  onIntererdCodeChange(): void {
    if (this.form.invalid || (this.timeIsExpired && this.sendType !== ConfirmCodeSendType.TwoFactorAuthentication)) {
      this.secureCodeEmit.emit('');
      return;
    }
    this.secureCodeEmit.emit(this.f.secureCode.value);
  }

  onTimerFinished(): void {
    this.timeIsExpired = true;
    this.form.patchValue({ secureCode: '' });
    this.form.markAsUntouched();
  }

  private initConfirmCode(): void {

    if (!this.option) {
      LogHelper.log('SECURITY => ERROR, OPTION IS NULL', this.option);
      return;
    }

    this.userInfoService.getUserInfo$()
      .pipe(take(1))
      .subscribe((userInfo: UserInfo) => {

        if (!userInfo || !userInfo.securityCode) {
          LogHelper.log('SECURITY => REQUEST INFO DATA NULL');
          return;
        }

        LogHelper.log('SECURITY => STEP 2: Handel add mode: ', userInfo);
        this.userInfoData = userInfo;
        this.getConfirmCodeByOptionConfirmType();
        const value = ((this.sendType === ConfirmCodeSendType.Email)
          ? this.userInfoData.information.userName : this.userInfoData.information.phoneNumber.replace('_', ' '));

        this.form.patchValue({ email: value });
      });
  }

  private getConfirmCodeByOptionConfirmType(): void {

    const type: ConfirmCodeType = this.option.confirmType;

    switch (type) {
      case ConfirmCodeType.REQUEST_SECURITY_CHANGE:
        this.getChangeRequestSettingConfirmCodeData();
        break;

      case ConfirmCodeType.DEPOSIT_REQUEST:
      case ConfirmCodeType.WITHDRAWAL_REQUEST:
      case ConfirmCodeType.INTERNAL_TRANSFER_REQUEST:
      case ConfirmCodeType.BETWEEN_MY_WALLETS:
      case ConfirmCodeType.BUY_AND_SELL_REQUEST:
      case ConfirmCodeType.EXCHANGE_SERVICE_REQUEST:
      case ConfirmCodeType.VOUCHER:
      case ConfirmCodeType.POINT_WITHDRAWAL_REQUEST:
      case ConfirmCodeType.SMS_PANEL_CHARGE_REQUEST:
        this.getRequestConfirmCodeData();
        break;
    }
  }

  private getChangeRequestSettingConfirmCodeData(): void {
    this.enableConfirmation = true;
    this.sendType = ConfirmCodeSendType.Email;
    this.activeConfirmCodeType = ConfirmCodeType.REQUEST_SECURITY_CHANGE;
  }

  private getRequestConfirmCodeData(): void {

    const scecurityType = this.getRequestSecurityTypeFromUserInformation();

    LogHelper.log('SECURITY => Request Security Type From User Information', scecurityType);

    this.getConfirmCodeDataBySecurityType(scecurityType);
  }

  private getRequestSecurityTypeFromUserInformation(): ConfirmCodeSecurityType {

    const userSecurityCode = this.userInfoData.securityCode;
    this.activeConfirmCodeType = this.option.confirmType;

    switch (this.option.confirmType) {
      case ConfirmCodeType.DEPOSIT_REQUEST:
        return userSecurityCode.deposit;

      case ConfirmCodeType.WITHDRAWAL_REQUEST:
        return userSecurityCode.withdrawal;

      case ConfirmCodeType.INTERNAL_TRANSFER_REQUEST:
        return userSecurityCode.send;

      case ConfirmCodeType.EXCHANGE_SERVICE_REQUEST:
        return userSecurityCode.exchange;

      case ConfirmCodeType.BETWEEN_MY_WALLETS:
        return userSecurityCode.exchange;

      case ConfirmCodeType.VOUCHER:
        return userSecurityCode.voucher;

      case ConfirmCodeType.POINT_WITHDRAWAL_REQUEST:
        return userSecurityCode.point;

      case ConfirmCodeType.SMS_PANEL_CHARGE_REQUEST:
        return userSecurityCode.smsPanelCharge;

      default:
        LogHelper.log('SECURITY => ERROR IN LOAD TYPE FROM STORE');
        return null;
    }
  }

  private getConfirmCodeDataBySecurityType(type: ConfirmCodeSecurityType): void {
    switch (type) {
      case ConfirmCodeSecurityType.Disabled:
        LogHelper.log('SECURITY => REQUEST SECURITY', 'DISABLE', type);
        this.securityIsDisabledEmit.emit(true);
        break;

      case ConfirmCodeSecurityType.SMS:
        LogHelper.log('SECURITY => REQUEST SECURITY', 'SMS', type);
        this.sendType = ConfirmCodeSendType.SMS;
        this.enableConfirmation = true;
        break;

      case ConfirmCodeSecurityType.Email:
        LogHelper.log('SECURITY => REQUEST SECURITY', 'EMAIL', type);
        this.sendType = ConfirmCodeSendType.Email;
        this.enableConfirmation = true;
        break;

      case ConfirmCodeSecurityType.TwoFactorAuthentication:
        LogHelper.log('SECURITY => REQUEST SECURITY', '2Fa', type);
        this.sendType = ConfirmCodeSendType.TwoFactorAuthentication;
        this.enableConfirmation = true;
        break;
    }
  }

  private onSecureCodeChange(): void {
    this.f.secureCode.valueChanges
      .subscribe(
        res => {
          setTimeout(
            () => {
              this.onIntererdCodeChange();
            }, 50
          );
        }
      );
  }

}
